import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '@/lib/firebase';

const BACKGROUNDS = ['/bg1.jpg', '/bg2.jpg', '/bg3.jpg', '/bg4.jpg'];

export function AuthActionPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isValidCode, setIsValidCode] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [bgImage, setBgImage] = useState(BACKGROUNDS[0]);

  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode');
  const oobCode = queryParams.get('oobCode');

  useEffect(() => {
    const randomBg = BACKGROUNDS[Math.floor(Math.random() * BACKGROUNDS.length)];
    setBgImage(randomBg);

    if (mode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setIsValidCode(true);
          setEmail(email);
        })
        .catch((err) => {
          setIsValidCode(false);
          setError(err.message || 'Invalid or expired password reset link.');
        });
    } else if ((mode === 'verifyAndChangeEmail' || mode === 'verifyEmail') && oobCode) {
      applyActionCode(auth, oobCode)
        .then(async () => {
          if (auth.currentUser) await auth.currentUser.getIdToken(true);
          setIsValidCode(false);
          setSuccessMessage('Your email address has been verified. Sign in with your updated email address.');
        })
        .catch((err) => {
          setIsValidCode(false);
          setError(err.message || 'This email verification link is invalid or has expired.');
        });
    } else {
      // If there's no mode or oobCode, they likely arrived here from the "Continue"
      // button of the default Firebase Auth reset page. We can just guide them to login.
      setIsValidCode(false);
      setSuccessMessage('Action completed successfully. You can now sign in.');
    }
  }, [mode, oobCode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (!oobCode) return;

    setIsSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccessMessage('Password has been reset successfully! Redirecting...');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="flex min-h-screen relative w-full h-full bg-cover bg-center bg-no-repeat overflow-hidden items-center"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="absolute inset-0 bg-gradient-to-tl from-black/80 via-black/30 to-black/10 mix-blend-multiply"></div>
      <div className="absolute inset-0 bg-brand-bg/10 mix-blend-overlay"></div>
      
      {/* Left Side: Welcome Text */}
      <div className="flex-1 hidden lg:flex flex-col justify-center px-12 lg:px-24 relative z-10">
        <div className="max-w-xl animate-in slide-in-from-left-8 fade-in duration-1000">
          <h2 className="text-3xl font-light text-white mb-2 tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Welcome to
          </h2>
          <h1 className="text-7xl font-bold text-white tracking-tighter drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
            Dexter
          </h1>
        </div>
      </div>
      
      {/* Bottom Left Logo */}
      <div className="absolute bottom-12 left-12 lg:left-24 z-10 hidden lg:block animate-in fade-in duration-1000">
        <img src="/HHTech1White.png" alt="Hammerhead Technologies" className="w-48 h-auto" />
      </div>

      {/* Right Side: Frosted Glass Form (Floating) */}
      <div className="flex-1 lg:flex-none lg:w-[22rem] flex flex-col justify-center relative z-10 px-6 py-8 lg:mr-24 bg-white/70 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.15)] border border-white/60 rounded-3xl animate-in slide-in-from-right-8 fade-in duration-1000">
        <div className="w-full max-w-[18rem] mx-auto">
          <div className="flex flex-col items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {(!mode && !oobCode) ? 'Welcome Back' : mode === 'verifyAndChangeEmail' || mode === 'verifyEmail' ? 'Verify Email' : 'Reset Password'}
            </h2>
            {email ? (
              <p className="text-sm text-gray-700 font-medium text-center">
                For {email}
              </p>
            ) : (!mode && !oobCode) ? (
              <p className="text-sm text-gray-700 font-medium text-center">
                Ready to log in
              </p>
            ) : mode === 'verifyAndChangeEmail' || mode === 'verifyEmail' ? (
              <p className="text-sm text-gray-700 font-medium text-center">Confirming your new email address</p>
            ) : (
              <p className="text-sm text-gray-700 font-medium text-center">
                Create a new password
              </p>
            )}
          </div>

          {error && (
            <div role="alert" className="mb-5 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {successMessage && (
            <div role="alert" className="mb-5 rounded-md bg-green-50 p-3 text-sm text-green-700">
              {successMessage}
            </div>
          )}

          {isValidCode === null && !error && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent"></div>
            </div>
          )}

          {isValidCode && !successMessage && (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label
                  htmlFor="new-password"
                  className="block text-[10px] font-medium uppercase tracking-widest text-brand-text/70 mb-2"
                >
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full rounded-md border border-brand-divider bg-white/60 px-4 py-2.5 text-brand-text shadow-sm focus:border-brand-text focus:outline-none focus:ring-1 focus:ring-brand-text sm:text-sm placeholder-brand-text/30 transition-colors"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-[10px] font-medium uppercase tracking-widest text-brand-text/70 mb-2"
                >
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full rounded-md border border-brand-divider bg-white/60 px-4 py-2.5 text-brand-text shadow-sm focus:border-brand-text focus:outline-none focus:ring-1 focus:ring-brand-text sm:text-sm placeholder-brand-text/30 transition-colors"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-6 w-full rounded-full bg-brand-button px-4 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-button focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Resetting…' : 'Reset Password'}
              </button>
            </form>
          )}

          {(!mode && !oobCode) && (
            <div className="flex flex-col items-center">
              <Link
                to="/login"
                className="w-full rounded-full bg-brand-button px-4 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-button focus:ring-offset-2 flex items-center justify-center"
              >
                Go to Sign in
              </Link>
            </div>
          )}

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-brand-divider/30"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white/50 px-3 text-brand-text/70 font-medium text-[10px] uppercase tracking-widest rounded-full backdrop-blur-md">Or</span>
              </div>
            </div>
            
            <p className="mt-6 text-center text-xs font-medium text-gray-700">
              Return to{' '}
              <Link
                to="/login"
                className="font-semibold text-brand-text hover:text-brand-accent transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
